import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { $t } from "~/i18n.js";

import {
  AppstoreOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { Button, Empty, Input, Select, Switch, Tag } from "antd";

import { HelpBubble } from "~/components/help/helpBubble.jsx";
import { getMaterialTab } from "~/redux/materiaal/materiaal.actions.js";
import { MaintenanceStatusTag } from "../../materialTags.jsx";
import { MatChapterCard } from "./sections/matChapterCard.jsx";
import { MatChapterModal } from "./sections/matChapterModal.jsx";
import { Radio } from "antd";
import { MatChapterList } from "./list/MatChapterList.jsx";

export const MaterialDocuments = ({ currentMaterial, chapters, readOnly }) => {
  // setup search for kisten you are allowed access to
  const [view, setView] = useState("list");

  const [search, setSearch] = useState("");
  const [searchYear, setSearchYear] = useState(null);
  const [searchTag, setSearchTag] = useState(null);

  
  const [readOnlyMode, setReadOnlyMode] = useState(true);
  const [openChapter, setOpenChapter] = useState(null);
  
  // reduce unique cluster names in tabs
  let allTags = {};
  chapters?.forEach((c) => (allTags[c.tag] = { name: c.tag }));
  
  const allTagsList = Object.keys(allTags);
  allTags = allTagsList.map((v) => ({value: v, label: v}));
  allTags.push({ label: $t("archief"), value: 'archive' });
  
  const dispatch = useDispatch();

  const saveChapter = async (data) => {
    const payload = { action: "chapter", ...data };

    return await dispatch(
      getMaterialTab({
        method: data.method,
        activeTab: "documents",
        materialID: currentMaterial.id,
        payload,
        as_json: false,
      })
    );
  };

  const [loadingOrder, setLoadingOrder] = useState(false);
  const save_chapter_order = async (chapters) => {
    setLoadingOrder(true);
    // Renumber sequentially to prevent order value drift
    const normalized = chapters.map((c, i) => ({ ...c, order: i }));
    const payload = {
      action: "chapter_order",
      chapters: normalized.map((c) => {
        return { id: c.id, order: c.order, name: c.name };
      }),
    };

    try {
      const data = await dispatch(
        getMaterialTab({
          method: "POST",
          activeTab: "documents",
          materialID: currentMaterial.id,
          payload,
        })
      );
      return data;
    } finally {
      setLoadingOrder(false);
    }
  }

  return (
    <div className="column" style={{ gap: 24, alignItems: "flex-start" }}>
      {openChapter && (
        <MatChapterModal
          allTags={allTagsList}
          closeModal={() => setOpenChapter(null)}
          chapter={openChapter}
          saveChapter={saveChapter}
        />
      )}

      <div className="row" style={{ gap: 12, flexWrap: "wrap", width: "100%" }}>
        <Input.Search
          style={{ maxWidth: 250 }}
          value={search}
          allowClear
          size="small"
          placeholder={$t("Zoek op naam")}
          onChange={(event) => setSearch(event.target.value.toLowerCase())}
        />

        <Select
          style={{ minWidth: 100 }}
          value={searchYear}
          allowClear
          showSearch
          size="small"
          placeholder={$t("Jaar")}
          onChange={(value) => setSearchYear(value)}
          options={Array.from({ length: 16 }, (_, i) => {
            const year = new Date().getFullYear() - i;
            return { value: String(year), label: String(year) };
          })}
        />

        <Select
          size='small'
          style={{ minWidth: 140 }}
          allowClear
          showSearch
          placeholder={$t("Tag")}
          value={searchTag}
          onChange={(value) => setSearchTag(value)}
          options={allTags}
        />

        {!readOnly && (
          <p className="row">
            {$t("Wijzig")}:
            <Switch
              size="small"
              style={{ marginLeft: 4 }}
              onChange={(checked) => setReadOnlyMode(!checked)}
              checked={!readOnlyMode}
            />
          </p>
        )}

        {!readOnlyMode && (
          <Button
            onClick={() =>
              setOpenChapter({
                material_id: currentMaterial.id,
                is_public: false,
                order: chapters.length + 1,
              })
            }
            size="small"
            icon={<PlusOutlined />}
          >
            {$t("Voeg toe")}
          </Button>
        )}

        <Radio.Group
          value={view}
          onChange={(e) => setView(e.target.value)}
          size="small"
          optionType="button"
          buttonStyle="solid"
          style={{ marginLeft: "auto" }}
        >
          <Radio.Button value="list">
            <UnorderedListOutlined />
          </Radio.Button>
          <Radio.Button value="grid">
            <AppstoreOutlined />
          </Radio.Button>
        </Radio.Group>
      </div>

      {currentMaterial?.document_status && (
        <div className="row" style={{ gap: 12 }}>
          <p className="label">
            {$t("Status")}{" "}
            <HelpBubble content={$t("Status word elke nacht bijgewerkt")} />
          </p>
          <MaintenanceStatusTag
            maintenance_status={currentMaterial.document_status}
            default_value="nvt"
          />
        </div>
      )}

      <div
        className={view === "list" ? "column" : "row"}
        style={{
          flexWrap: "wrap",
          alignItems: "flex-start",
          width: view === "list" ? "100%" : "auto",
        }}
      >
        {chapters?.length > 0 ? (
          chapters?.map((chapter, idx) => {
            // see if any documents are in chapter, otherwise filter out
            let matched_documents = chapter.documents;

            // filter out if archive
            if (searchTag !== 'archive' && chapter.is_archived) return null;
            if (searchTag === 'archive' && !chapter.is_archived) return null;

            if (searchTag && searchTag !== 'archive' && chapter.tag !== searchTag)
              return null;

            if (search) {
              matched_documents = matched_documents?.filter(
                (f) => f.name.toLowerCase().indexOf(search) > -1
              );
              if (matched_documents?.length == 0) return null;
            }

            if (searchYear) {
              matched_documents = matched_documents?.filter((d) => {
                if (d.name.indexOf(searchYear) > -1) {
                  return true
                }
                
                // might be that one of the files is in the year
                const file_in_year = d.files?.some((f) =>
                  f.date_created.startsWith(searchYear) || f.alias.indexOf(searchYear) > -1 || f.version.indexOf(searchYear) > -1
                );
                if (file_in_year) return true;
                
                return false;
              });
              if (matched_documents?.length == 0) return null;
            }

            return view === "grid" ? (
              <MatChapterCard
                key={chapter.id}
                chapter={chapter}
                documents={matched_documents}
                readOnly={readOnlyMode}
                setOpenChapter={setOpenChapter}
                allChapters={chapters}
                materialID={currentMaterial.id}
              />
            ) : (
              <MatChapterList
                idx={idx}
                loadingOrder={loadingOrder}
                save_chapter_order={save_chapter_order}
                key={chapter.id}
                chapter={chapter}
                documents={matched_documents}
                searchYear={searchYear}
                readOnly={readOnlyMode}
                setOpenChapter={setOpenChapter}
                allChapters={chapters}
                materialID={currentMaterial.id}
              />
            );
          })
        ) : (
          <Empty description="Geen documenten" />
        )}
      </div>
    </div>
  );
};
